import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import type { TopicCondition as TopicConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

export const TopicCondition = memo(function TopicCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: TopicConditionType;
  onChange: (updated: TopicConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const topicsEditable =
    editableFields === undefined || editableFields.has("topics");

  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const topicsLoad = useLoad(
    () => supportRpcClient.getConversationTopics({}),
    [supportRpcClient],
  );

  const topics = useMemo(
    () => topicsLoad.value?.topics ?? [],
    [topicsLoad.value],
  );

  const topicItems: RedoListItem<string>[] = useMemo(() => {
    return topics.map((topic) => ({
      id: topic,
      type: "text" as const,
      text: topic,
      value: topic,
    }));
  }, [topics]);

  const selectedItems: RedoListItem<string>[] = useMemo(() => {
    return (condition.topics || []).map((topic) => ({
      id: topic,
      type: "text" as const,
      text: topic,
      value: topic,
    }));
  }, [condition.topics]);

  const displayItem: RedoListItem<string> | undefined = useMemo(() => {
    if (selectedItems.length === 0) {
      return undefined;
    }
    if (selectedItems.length === 1) {
      return selectedItems[0];
    }
    return {
      id: "display",
      type: "text" as const,
      text: `${selectedItems.length} topics selected`,
      value: "",
    };
  }, [selectedItems]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="tertiary">
        Topic
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={!topicsEditable}
        isDropdownOpen={dropdownOpen}
        onClick={() => topicsEditable && setDropdownOpen(!dropdownOpen)}
        placeholder="Select topics"
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={topicItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!topicsEditable) {
            return;
          }
          onChange({ ...condition, topics: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
